import React from 'react';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { MessageBar } from '@bingads-webui-react/fabric-control';
import { Pagination } from '@bingads-webui-react/pagination';
import { SearchBoxWithLiveResults, getSearchResultLiveRegionText } from '@bingads-webui-react/search-box';
import { ListSelector } from '@bingads-webui-react/list-selector';

export class EntitySelector extends React.Component {
  static defaultProps = {
    pageSize: 10,
    selectedItem: null,
    renderHeader: undefined,
    renderEmptyList: undefined,
    renderActionBar: undefined,
    renderFooter: undefined,
    isShowSearchbox: true,
    enableRandomPage: false,
    enableArrowKeysSupport: false,
    ariaLabel: '',
    searchBoxAriaLabel: '',
    searchBoxPlaceholder: '',
    checkSelected: (selectedItem, item) => _.isEqual(selectedItem, item),
    enableOverflowTooltip: false,
    messageConfig: {},
    previousComponent: null,
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const { pageNumber } = nextProps;
    if (pageNumber !== undefined && pageNumber !== prevState.pageNumber) {
      return {
        pageNumber,
      };
    }
    return null;
  }

  state = {
    pageNumber: 0,
    query: '',
  };

  constructor(props) {
    super(props);
    this.paginationRef = React.createRef();
    this.searchBoxRef = React.createRef();
  }

  onPageNumberChanged = (pageNumber) => {
    this.onFetch({ query: this.state.query, pageNumber });
  };

  onQueryChange = (query) => {
    this.setState({ query });
  };

  onSearch = (query) => {
    this.onFetch({ query, pageNumber: 0 });
  };

  onFetch = ({ query, pageNumber }) => {
    this.setState({
      pageNumber,
      query,
    });

    this.props.onFetch({ query, pageNumber });
  };

  onListSelectorKeyDown = (event) => {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        // Handle Shift+Tab key press
        if (this.props.previousComponent && this.props.previousComponent.focus) {
          event.preventDefault(); // Prevent the default tab behavior
          event.stopPropagation(); // Prevent the event from bubbling up
          this.props.previousComponent.focus();
        } else if (this.props.isShowSearchbox && this.searchBoxRef.current) {
          const searchIcon = this.searchBoxRef.current.querySelector('.search-box__icon');
          if (searchIcon && searchIcon.focus) {
            searchIcon.focus();
          }
        }
      } else if (this.paginationRef.current) {
        const prevButton = this.paginationRef.current.querySelector('.btn.btn-default.prev');
        const nextButton = this.paginationRef.current.querySelector('.btn.btn-default.next');
        event.preventDefault(); // Prevent the default tab behavior
        event.stopPropagation(); // Prevent the event from bubbling up
        if (this.paginationRef.current.querySelector('.btn.btn-default.prev[disabled]')) {
          if (nextButton && nextButton.focus) {
            nextButton.focus();
          }
        } else if (prevButton && prevButton.focus) {
          prevButton.focus();
        }
      }
    }
  };

  renderSearchbox = () => {
    const {
      i18n, isShowSearchbox, totalCount, searchBoxPlaceholder,
    } = this.props;
    if (isShowSearchbox) {
      return (
        <div className="search-box-container" ref={this.searchBoxRef}>
          <SearchBoxWithLiveResults
            ariaLabel={this.props.searchBoxAriaLabel}
            onQueryChange={this.onQueryChange}
            onSearch={this.onSearch}
            value={this.state.query}
            placeholder={searchBoxPlaceholder || i18n.getString('EntitySelector_Search_Placeholder')}
            screenReaderText={getSearchResultLiveRegionText(
              totalCount,
              i18n.getString('No results found for {{searchText}}', { searchText: this.state.query }),
              i18n.getString('Found {{totalCount}} Results for {{searchText}}', { totalCount, searchText: this.state.query })
            )}
            aria-live="assertive"
          />
        </div>
      );
    }
    return null;
  };

  render() {
    const { i18n, messageConfig } = this.props;
    return (
      <div className="ba-entity-selector">
        {this.renderSearchbox()}

        <div className="list-selector-container">
          {
            !_.isEmpty(this.props.messageConfig) &&
            <MessageBar className="list_selector_message_bar">
              {messageConfig.text}

            </MessageBar>
          }
          <ListSelector
            renderHeader={this.props.renderHeader}
            renderItem={this.props.renderItem}
            renderActionBar={this.props.renderActionBar}
            renderFooter={this.props.renderFooter}
            onSelect={this.props.onSelect}
            data={this.props.items}
            onKeyDown={this.onListSelectorKeyDown}
            renderEmptyList={this.props.renderEmptyList}
            selectedItem={this.props.selectedItem}
            checkSelected={this.props.checkSelected}
            enableArrowKeysSupport={this.props.enableArrowKeysSupport}
            ariaLabel={this.props.ariaLabel}
            enableOverflowTooltip={this.props.enableOverflowTooltip}
          />
        </div>

        {
          this.props.totalCount > this.props.pageSize &&
          <div className="pagination-container" ref={this.paginationRef}>
            <Pagination
              onPageNumberChanged={this.onPageNumberChanged}
              pageNumber={this.state.pageNumber}
              itemCount={this.props.totalCount}
              pageSize={this.props.pageSize}
              pageText={i18n.getString('EntitySelector_Pagination_Page')}
              totalPageCountText={i18n.getString('EntitySelector_Pagination_TotalPageCountText')}
              previousPageText={i18n.getString('EntitySelector_Pagination_PreviousPageText')}
              nextPageText={i18n.getString('EntitySelector_Pagination_NextPageText')}
              i18n={i18n}
              enableRandomPage={this.props.enableRandomPage}
            />
          </div>
        }
      </div>
    );
  }
}

EntitySelector.propTypes = {
  /**
   * item count per page
   */
  pageSize: PropTypes.number,
  /**
   * function to fetch items
   */
  onFetch: PropTypes.func.isRequired,
  /**
   * callback to pass selected item and it's index outside
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * selected item
   */
  selectedItem: PropTypes.objectOf(PropTypes.any),
  /**
   * Rendered at the top of all the items. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered Header element
   */
  renderHeader: PropTypes.func,
  /**
   * Takes an item from `data` and renders it into the list,
   * provides additional metadata like `index` if you need it.
   *
   * @param {Object} item - item among data
   * @param {number} index - item index
   * @return {ReactElement} Rendered item element
   */
  renderItem: PropTypes.func.isRequired,
  /**
   * Rendered when the list is empty. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered empty list element
   */
  renderEmptyList: PropTypes.func,

  /**
   * Rendered at the top of all the items. A render function that returns a React element.
   * while omitted, will not render
   *
   * @return {ReactElement} Rendered item element
   */
  renderActionBar: PropTypes.func,
  /**
   * Rendered at the bottom of all the items. A render function that returns a React element.
   *
   * @return {ReactElement} Rendered Footer element
   */
  renderFooter: PropTypes.func,
  /**
   * A array of data.
   */
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * total item count, which might not equal
   */
  totalCount: PropTypes.number.isRequired,
  /**
   * make the searchbox configable
   */
  isShowSearchbox: PropTypes.bool,
  /**
   * enable random page
   */
  enableRandomPage: PropTypes.bool,
  /**
   * i18n for localization
   */
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    parseDecimal: PropTypes.func.isRequired,
  }).isRequired,
  /**
   * Used to check whether an item is selected.
   *
   * @param {Object} selectedItem - selected item
   * @param {Object} item - single data in the list
   * @return {boolean} - whether current item is selected
   */
  checkSelected: PropTypes.func,
  /**
   * Enable arrow keys support
   */
  enableArrowKeysSupport: PropTypes.bool,
  /**
   * Used to specify aria label for the list box.
   *
   */
  ariaLabel: PropTypes.string,
  /**
   * Used to specify aria label for the search box input.
   *
   */
  searchBoxAriaLabel: PropTypes.string,
  /**
   * Used to specify placeholder for the search box input.
   *
   */
  searchBoxPlaceholder: PropTypes.string,
  /**
   * Used to enable the overflow tooltip for list item.
   *
   */
  enableOverflowTooltip: PropTypes.bool,
  /**
   * Used to config message bar
   */
  messageConfig: PropTypes.shape({}),
  /**
   * Used to go back focus to previous component
   */
  previousComponent: PropTypes.shape({
    focus: PropTypes.func,
  }),
};
